import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, CalendarDays, CheckCircle, AlertTriangle, X, Plus, DollarSign, User, FileText, Banknote, Lock, Shield, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const getCardType = (number) => {
  const firstDigit = number.charAt(0);
  const firstTwoDigits = number.substring(0, 2);
  
  if (firstDigit === '4') return 'Visa';
  if (firstTwoDigits >= '51' && firstTwoDigits <= '55') return 'Mastercard';
  if (firstDigit === '3' && (number.charAt(1) === '4' || number.charAt(1) === '7')) return 'Amex';
  if (firstDigit === '6') return 'Discover';
  
  return 'Card';
};

const EnhancedPaymentPortal = ({ agentId, onTransactionComplete }) => {
  const { user, logout } = useAuth();
  
  // Client info & account linking
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [existingClient, setExistingClient] = useState(null);
  const [clientHistory, setClientHistory] = useState([]);
  
  // Payment type and policy
  const [paymentType, setPaymentType] = useState('New Business');
  const [policyNumber, setPolicyNumber] = useState('');
  
  // Amount breakdown with premium protection
  const [premium, setPremium] = useState('');
  const [brokerFee, setBrokerFee] = useState('');
  const [minimumPremiumAmount, setMinimumPremiumAmount] = useState(0);
  
  // Payment splits
  const [chargeNowSplits, setChargeNowSplits] = useState([]);
  const [scheduledPayments, setScheduledPayments] = useState([]);
  
  // Client's saved cards
  const [clientCards, setClientCards] = useState([]);
  const [tempCards, setTempCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  
  // PIN Authorization System
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinCode, setPinCode] = useState('');
  const [pinPurpose, setPinPurpose] = useState('');
  const [pendingCashAction, setPendingCashAction] = useState(null);
  const [pinError, setPinError] = useState('');
  
  // Card entry modal
  const [showCardModal, setShowCardModal] = useState(false);
  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const [currentCardOperation, setCurrentCardOperation] = useState({ type: '', id: null });
  const [newCardData, setNewCardData] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  
  // UI state
  const [isValid, setIsValid] = useState(true);
  const [validationMessage, setValidationMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  
  // Calculate totals with premium protection
  const totalDue = paymentType === 'Monthly Payment' 
    ? 5 // Fixed $5 processing fee for monthly payments
    : parseFloat(premium || 0) + parseFloat(brokerFee || 0);
  const totalChargeNow = chargeNowSplits.reduce((sum, split) => sum + parseFloat(split.amount || 0), 0);
  const totalScheduled = scheduledPayments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
  const remainingAmount = totalDue - totalChargeNow - totalScheduled;
  
  // Corrected logic: CC to COMP as payment method, not separate checkbox
  const premiumAmount = parseFloat(premium || 0);
  const brokerFeeAmount = parseFloat(brokerFee || 0);
  
  // Calculate CC to COMP amounts from payment splits
  const ccToCompChargeNow = chargeNowSplits
    .filter(split => split.paymentMethod === 'cc_to_comp')
    .reduce((sum, split) => sum + parseFloat(split.amount || 0), 0);
  
  const ccToCompScheduled = scheduledPayments
    .filter(payment => payment.paymentMethod === 'cc_to_comp')
    .reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
  
  const totalCcToComp = ccToCompChargeNow + ccToCompScheduled;
  
  // Calculate actual charges (excluding CC to COMP)
  const actualChargeNow = chargeNowSplits
    .filter(split => split.paymentMethod !== 'cc_to_comp')
    .reduce((sum, split) => sum + parseFloat(split.amount || 0), 0);
  
  const actualScheduled = scheduledPayments
    .filter(payment => payment.paymentMethod !== 'cc_to_comp')
    .reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
  
  // Balance validation - all money must be accounted for
  const totalAllocated = totalChargeNow + totalScheduled; // Includes CC to COMP amounts
  const totalShortfall = totalDue - totalAllocated;
  
  // Search for existing clients
  const searchExistingClient = useCallback(async (searchData = null) => {
    const searchParams = searchData || {
      name: clientName,
      email: clientEmail,
      phone: clientPhone,
      policyNumber: policyNumber
    };
    
    // Only search if at least one field has data
    if (!searchParams.name && !searchParams.email && !searchParams.phone && !searchParams.policyNumber) {
      return;
    }
    
    try {
      const response = await axiosInstance.post('/api/clients/search', searchParams);
      
      if (response.data.client) {
        setExistingClient(response.data.client);
        setClientHistory(response.data.paymentHistory || []);
        setClientCards(response.data.savedCards || []);
        
        // Don't auto-fill - let user click "Use This Client" button
      } else {
        // No existing client found
        setExistingClient(null);
        setClientHistory([]);
        setClientCards([]);
      }
    } catch (error) {
      console.log('Client search failed:', error);
      setExistingClient(null);
      setClientHistory([]);
      setClientCards([]);
    }
  }, [clientName, clientEmail, clientPhone, policyNumber]);
  
  // Format phone number to (000) 000-0000
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };
  
  // Handle phone input formatting
  const handlePhoneChange = (value) => {
    const formatted = formatPhoneNumber(value);
    setClientPhone(formatted);
  };
  
  // PIN Authorization System
  const requestPinAuthorization = async (purpose, action) => {
    setPinPurpose(purpose);
    setPendingCashAction(action);
    setShowPinModal(true);
    setPinError('');
    setPinCode('');

    // Send SMS notification to admins about PIN request
    try {
      await axiosInstance.post('/api/admin/pin-notification', {
        agentName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'Unknown Agent',
        agentId: agentId || user?._id || 'Unknown',
        clientName: clientName || existingClient?.name || 'Unknown Client',
        amount: purpose.includes('$') ? purpose.split('$')[1].split(' ')[0] : 'Unknown',
        paymentType: paymentType || 'Unknown',
        transactionId: `PENDING-${Date.now()}`,
        purpose: purpose
      });
    } catch (error) {
      console.error('Failed to send PIN notification:', error);
      // Don't block PIN entry if SMS fails
    }
  };
  
  const validatePin = async () => {
    // In production, this would validate against a secure PIN system
    const validPins = ['1234', '0000', '9999', agentId]; // Agent-specific PINs
    
    if (validPins.includes(pinCode)) {
      setShowPinModal(false);
      
      // Execute the pending cash action
      if (pendingCashAction) {
        pendingCashAction();
        setPendingCashAction(null);
      }
      
      setPinCode('');
      setPinError('');
    } else {
      setPinError('Invalid PIN. Please try again.');
      setPinCode('');
    }
  };
  
  // Enhanced card management
  const openCardModal = useCallback((type, id) => {
    setCurrentCardOperation({ type, id });
    setShowCardModal(true);
    setShowBillingAddress(true); // Always show billing address
    setNewCardData({
      cardNumber: '',
      cardName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      address: '',
      city: '',
      state: '',
      zip: '',
    });
  }, []);
  
  const saveNewCard = useCallback(async () => {
    if (newCardData.cardNumber.length < 15 || 
        newCardData.cardName.trim() === '' || 
        !newCardData.expiryMonth || 
        !newCardData.expiryYear ||
        newCardData.cvv.trim() === '' ||
        newCardData.address.trim() === '' ||
        newCardData.city.trim() === '' ||
        newCardData.state.trim() === '' ||
        newCardData.zip.trim() === '') {
      alert('Please fill in all required card details including billing address');
      return;
    }
    
    try {
      const cardData = {
        customerId: existingClient?._id,
        customerName: clientName,
        customerEmail: clientEmail,
        customerPhone: clientPhone,
        cardNumber: newCardData.cardNumber,
        expiryMonth: parseInt(newCardData.expiryMonth),
        expiryYear: parseInt(newCardData.expiryYear),
        cvv: newCardData.cvv,
        billingAddress: {
          street: newCardData.address,
          city: newCardData.city,
          state: newCardData.state,
          zip: newCardData.zip
        }
      };
      
      const response = await axiosInstance.post('/api/payments/cards', cardData);
      const savedCard = response.data.card;
      
      // Update local state
      if (existingClient) {
        setClientCards(prev => [...prev, savedCard]);
      } else {
        setTempCards(prev => [...prev, savedCard]);
      }
      
      // Update the payment split/scheduled payment
      if (currentCardOperation.type === 'chargeNow') {
        setChargeNowSplits(prev => prev.map(split => 
          split.id === currentCardOperation.id 
            ? { ...split, cardId: savedCard.cardId }
            : split
        ));
      } else if (currentCardOperation.type === 'scheduled') {
        setScheduledPayments(prev => prev.map(payment => 
          payment.id === currentCardOperation.id 
            ? { ...payment, cardId: savedCard.cardId }
            : payment
        ));
      }
      
      setShowCardModal(false);
    } catch (error) {
      console.error('Failed to save card:', error);
      alert(`Failed to save card: ${error.response?.data?.error || error.message}`);
    }
  }, [newCardData, currentCardOperation, existingClient, clientName, clientEmail, clientPhone, agentId]);
  
  // Remove card functionality
  const removeCard = useCallback(async (cardId) => {
    if (!confirm('Are you sure you want to remove this payment method? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/api/payments/cards/${cardId}`);
      
      // Update local state
      setClientCards(prev => prev.filter(card => card.cardId !== cardId));
      setTempCards(prev => prev.filter(card => card.cardId !== cardId));
      
      // Update any payment splits using this card
      setChargeNowSplits(prev => prev.map(split => 
        split.cardId === cardId ? { ...split, cardId: 'new' } : split
      ));
      setScheduledPayments(prev => prev.map(payment => 
        payment.cardId === cardId ? { ...payment, cardId: 'new' } : payment
      ));
      
    } catch (error) {
      console.error('Failed to remove card:', error);
      alert('Failed to remove card. Please try again.');
    }
  }, []);
  
  // Select card functionality
  const selectCard = useCallback((card) => {
    setSelectedCard(card);
  }, []);
  
  // Form reset function
  const resetForm = useCallback(() => {
    setClientName('');
    setClientEmail('');
    setClientPhone('');
    setPolicyNumber('');
    setPremium('');
    setBrokerFee('');
    setChargeNowSplits([]);
    setScheduledPayments([]);
    setExistingClient(null);
    setClientHistory([]);
    setClientCards([]);
    setTempCards([]);
    setShowSuccessModal(false);
    setLastTransaction(null);
  }, []);
  
  // Download receipt function
  const downloadReceipt = useCallback(async (paymentId) => {
    if (!lastTransaction) return;
    
    try {
      const response = await axiosInstance.get(
        `/api/payments/receipt/${lastTransaction.transactionId}/${paymentId}`,
        { responseType: 'blob' }
      );
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${lastTransaction.transactionId}-${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    }
  }, [lastTransaction]);

  // Download payment plan function
  const downloadPaymentPlan = useCallback(async (transactionId) => {
    if (!transactionId) return;
    
    try {
      const response = await axiosInstance.get(
        `/api/payments/payment-plan/${transactionId}`,
        { responseType: 'blob' }
      );
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PaymentPlan_${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading payment plan:', error);
      if (error.response) {
        alert(`Failed to download payment plan: ${error.response.data?.message || error.response.statusText}`);
      } else {
        alert('Failed to download payment plan. Please check your connection and try again.');
      }
    }
  }, []);
  
  // Enhanced payment split management
  const addChargeNowSplit = useCallback(() => {
    const suggestedAmount = remainingAmount > 0 ? remainingAmount.toFixed(2) : '';
    const newSplit = { 
      id: Date.now(), 
      amount: suggestedAmount, 
      cardId: 'new',
      paymentMethod: 'card'
    };
    setChargeNowSplits(prev => [...prev, newSplit]);
  }, [remainingAmount]);
  
  const updateChargeNowSplit = useCallback((id, field, value) => {
    if (field === 'paymentMethod' && value === 'cash') {
      // No PIN required for charge now cash payments
      setChargeNowSplits(prev => prev.map(split => 
        split.id === id ? { ...split, [field]: value, cardId: 'cash' } : split
      ));
    } else if (field === 'paymentMethod' && value === 'cc_to_comp') {
      // CC to COMP payments - only available for charge now
      setChargeNowSplits(prev => prev.map(split => 
        split.id === id ? { ...split, [field]: value, cardId: 'cc_to_comp' } : split
      ));
    } else {
      setChargeNowSplits(prev => prev.map(split => 
        split.id === id ? { ...split, [field]: value } : split
      ));
    }
  }, [chargeNowSplits]);
  
  const addScheduledPayment = useCallback(() => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    
    const suggestedAmount = remainingAmount > 0 ? remainingAmount.toFixed(2) : '';
    const newPayment = { 
      id: Date.now(), 
      date: dateStr, 
      amount: suggestedAmount, 
      cardId: 'new',
      paymentMethod: 'card',
      isAutoCalculated: false
    };
    setScheduledPayments(prev => [...prev, newPayment]);
  }, [remainingAmount]);
  
  const updateScheduledPayment = useCallback((id, field, value) => {
    if (field === 'paymentMethod' && value === 'cash') {
      // Require PIN for scheduled cash payments
      const payment = scheduledPayments.find(p => p.id === id);
      const paymentAmount = parseFloat(payment?.amount || 0);
      
      requestPinAuthorization(
        `Authorize scheduled cash payment: ${formatCurrency(paymentAmount)}`,
        () => {
          setScheduledPayments(prev => prev.map(payment => 
            payment.id === id ? { ...payment, [field]: value, cardId: 'cash' } : payment
          ));
        }
      );
    } else {
      // CC to COMP is not available for scheduled payments
      setScheduledPayments(prev => prev.map(payment => 
        payment.id === id ? { ...payment, [field]: value } : payment
      ));
    }
  }, [scheduledPayments]);
  
  const removeChargeNowSplit = useCallback((id) => {
    setChargeNowSplits(prev => prev.filter(split => split.id !== id));
  }, []);
  
  const removeScheduledPayment = useCallback((id) => {
    setScheduledPayments(prev => prev.filter(payment => payment.id !== id));
  }, []);
  
  // Simplified validation - just check if all money is accounted for
  useEffect(() => {
    // Standard validation first
    if (totalDue === 0) {
      setIsValid(true);
      setValidationMessage('Enter payment details');
      return;
    }
    
    // All money must be accounted for (including CC to COMP)
    if (Math.abs(totalShortfall) > 0.01) {
      setIsValid(false);
      setValidationMessage(`⚠️ All amounts must be covered. Remaining: ${formatCurrency(Math.abs(totalShortfall))}`);
      return;
    }
    
    // Success state with breakdown
    let message = '✅ Payment allocation complete: ';
    const parts = [];
    
    if (actualChargeNow > 0) {
      parts.push(`Charge Now ${formatCurrency(actualChargeNow)}`);
    }
    if (actualScheduled > 0) {
      parts.push(`Scheduled ${formatCurrency(actualScheduled)}`);
    }
    if (totalCcToComp > 0) {
      parts.push(`CC to COMP ${formatCurrency(totalCcToComp)}`);
    }
    
    message += parts.join(' + ');
    
    setIsValid(true);
    setValidationMessage(message);
  }, [totalDue, totalShortfall, actualChargeNow, actualScheduled, totalCcToComp]);
  
  // Auto-create default payment split
  useEffect(() => {
    if (totalDue > 0 && chargeNowSplits.length === 0 && scheduledPayments.length === 0) {
      const defaultSplit = { 
        id: Date.now(), 
        amount: totalDue.toFixed(2), 
        cardId: 'new',
        paymentMethod: 'card'
      };
      setChargeNowSplits([defaultSplit]);
    }
  }, [totalDue, chargeNowSplits.length, scheduledPayments.length]);
  
  // Removed auto-scheduling logic - agents must manually create scheduled payments
  
  // Enhanced form submission
  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    
    if (!isValid) {
      alert('Please ensure payment amounts are valid.');
      return;
    }
    
    if (!clientName || !clientEmail) {
      alert('Please enter client name and email.');
      return;
    }
    
    if (paymentType === 'Endorsement' && !policyNumber) {
      alert('Please enter policy number for endorsement.');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const paymentData = {
        // Client information (linked account)
        customerName: clientName,
        customerEmail: clientEmail,
        customerPhone: clientPhone,
        existingClientId: existingClient?._id,
        
        // Agent info
        agentId,
        
        // Policy details
        paymentType,
        policyNumber: paymentType === 'Endorsement' ? policyNumber : undefined,
        
        // Amounts
        premium: paymentType === 'Monthly Payment' ? 0 : parseFloat(premium || 0),
        brokerFee: paymentType === 'Monthly Payment' ? 5 : parseFloat(brokerFee || 0),
        totalAmount: totalDue,
        
        // Payment splits
        chargeNowSplits: chargeNowSplits.map(split => ({
          ...split,
          amount: parseFloat(split.amount)
        })),
        
        // Scheduled payments
        scheduledPayments: scheduledPayments.map(payment => ({
          ...payment,
          amount: parseFloat(payment.amount)
        })),
        
        // HCC declined tracking data for integration
        hccTrackingEnabled: true,
        ghlContactId: existingClient?.ghlContactId,
        
        // GHL Policy Tagging for Endorsements
        ghlTags: paymentType === 'Endorsement' && policyNumber ? [
          { name: 'policy_number', value: policyNumber },
          { name: 'payment_type', value: 'endorsement' },
          { name: 'agent_id', value: agentId }
        ] : [
          { name: 'payment_type', value: paymentType.toLowerCase().replace(' ', '_') },
          { name: 'agent_id', value: agentId }
        ],
        
        // CC to COMP data (corrected)
        ccToCompEnabled: totalCcToComp > 0,
        ccToCompAmount: totalCcToComp,
        actualChargeAmount: actualChargeNow,
        actualScheduledAmount: actualScheduled
      };
      
      const response = await axiosInstance.post('/api/payments/enhanced-process', paymentData);
      
      if (response.data.success) {
        // Store transaction data for success modal
        setLastTransaction(response.data);
        setShowSuccessModal(true);
        
        if (onTransactionComplete) {
          onTransactionComplete({ 
            success: true, 
            transactionId: response.data.transactionId,
            hccDeclined: response.data.hccDeclined
          });
        }
      } else {
        alert(response.data.message || 'Payment processing failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(error.response?.data?.message || 'Payment processing failed');
    } finally {
      setIsProcessing(false);
    }
  }, [isValid, clientName, clientEmail, clientPhone, paymentType, policyNumber, premium, brokerFee, totalDue, chargeNowSplits, scheduledPayments, agentId, existingClient, onTransactionComplete]);
  
  // Get all available cards for selection
  const getAllAvailableCards = () => {
    return [...clientCards, ...tempCards];
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Navigation */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="bg-blue-600 p-3 rounded-lg mr-4">
                <DollarSign size={32} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Payment Processor</h1>
                <p className="text-gray-600 mt-1">
                  {user?.firstName} {user?.lastName} - Agent ID: {user?.agentId || agentId}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={logout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <div className="border-t border-gray-200">
            <nav className="flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => window.location.href = '/agent'}
                className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors"
              >
                Dashboard
              </button>
              <button
                onClick={() => window.location.href = '/payment'}
                className="border-transparent text-blue-600 border-b-2 border-blue-600 py-4 px-1 text-center font-medium text-sm"
              >
                Payment Processor
              </button>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-gradient-to-b from-blue-50 to-white rounded-xl shadow-lg border border-blue-100 p-6">
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Client Info with Account Linking */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <User size={20} className="text-blue-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-800">Client Account</h2>
            </div>
            <div className="flex items-center space-x-2">
              {existingClient && (
                <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                  Found Account
                </div>
              )}
              <button
                onClick={() => setShowClientSearch(!showClientSearch)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
              >
                {showClientSearch ? 'Hide Search' : 'Search Existing Clients'}
              </button>
            </div>
          </div>
          
          {/* Client Search Section */}
          {showClientSearch && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-medium text-blue-800 mb-3">Search Existing Client</h3>
              <p className="text-sm text-blue-600 mb-4">
                Search by email or phone number (e.g., john@email.com or 619-368-7040)
              </p>
              
              {existingClient && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-2">
                    <CheckCircle size={20} className="text-green-600 mr-2" />
                    <h4 className="font-medium text-green-800">Client Found!</h4>
                  </div>
                  <div className="text-sm text-green-700">
                    <p className="mb-1"><strong>Name:</strong> {existingClient.name}</p>
                    <p className="mb-1"><strong>Email:</strong> {existingClient.email}</p>
                    {existingClient.phone && <p className="mb-1"><strong>Phone:</strong> {existingClient.phone}</p>}
                    {clientHistory.length > 0 && (
                      <p><strong>Previous Payments:</strong> {clientHistory.length} transactions</p>
                    )}
                    {clientCards.length > 0 && (
                      <p><strong>Saved Cards:</strong> {clientCards.length} payment methods</p>
                    )}
                  </div>
                </div>
              )}
              
              {(clientEmail || clientPhone) && !existingClient && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-2">
                    <AlertTriangle size={20} className="text-yellow-600 mr-2" />
                    <h4 className="font-medium text-yellow-800">New Client</h4>
                  </div>
                  <p className="text-sm text-yellow-700">
                    No existing client found. This will create a new client record.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <div className="space-y-6">
            {/* Simple Client Search */}
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={clientEmail}
                onChange={(e) => {
                  setClientEmail(e.target.value);
                  // Auto-search as they type but don't auto-fill
                  if (e.target.value.length > 2) {
                    // Check if it's a phone number (contains digits)
                    const cleanValue = e.target.value.replace(/\D/g, '');
                    if (cleanValue.length >= 10) {
                      // Search by phone
                      searchExistingClient({ phone: cleanValue });
                    } else if (e.target.value.includes('@')) {
                      // Search by email
                      searchExistingClient({ email: e.target.value });
                    }
                  } else {
                    setExistingClient(null);
                  }
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="Search by email or phone number..."
              />
            </div>

            {/* Client Found or Create New */}
            {existingClient ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center mb-2">
                      <CheckCircle size={20} className="text-green-600 mr-2" />
                      <h3 className="font-semibold text-green-800">Client Found</h3>
                    </div>
                    <div className="text-green-700">
                      <div className="font-medium">{existingClient.name}</div>
                      <div>{existingClient.email}</div>
                      {existingClient.phone && <div>{existingClient.phone}</div>}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setClientName(existingClient.name);
                      setClientPhone(formatPhoneNumber(existingClient.phone));
                      setClientEmail(existingClient.email);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Use This Client
                  </button>
                </div>
              </div>
            ) : clientEmail && clientEmail.length > 2 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex items-center">
                    <Plus size={20} className="text-blue-600 mr-2" />
                    <div>
                      <h3 className="font-semibold text-blue-800">New Client</h3>
                      <p className="text-blue-600 text-sm">No existing client found - please fill out the details below</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Client Details Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="John Smith"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="john@email.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                <input
                  type="tel"
                  value={clientPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="(000) 000-0000"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Auto-formatted for GHL compatibility</p>
              </div>
            </div>
            
            {/* Client History */}
            {clientHistory.length > 0 && (
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Payments</h4>
                <div className="max-h-20 overflow-y-auto">
                  {clientHistory.slice(0, 3).map((payment, index) => (
                    <div key={index} className="text-xs text-gray-600 mb-1">
                      {formatCurrency(payment.amount)} - {new Date(payment.date).toLocaleDateString()}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Card Management Section */}
        {(clientEmail || clientName) && (
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Payment Methods</h2>
              <button
                onClick={() => openCardModal('manage', null)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
              >
                + Add Card
              </button>
            </div>
            
            {/* Horizontal Card Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {[...clientCards, ...tempCards].map((card) => (
                <div
                  key={card.cardId}
                  onClick={() => selectCard(card)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedCard?.cardId === card.cardId 
                      ? 'border-blue-500 bg-blue-50 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <CreditCard size={24} className="text-gray-600" />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCard(card.cardId);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 rounded-full"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="font-medium text-gray-900 mb-1">
                    {card.displayData?.cardType || 'Card'} •••• {card.displayData?.last4}
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    {card.displayData?.cardholderName}
                  </div>
                  <div className="text-xs text-gray-500">
                    Exp {card.displayData?.expiryMonth ? String(card.displayData.expiryMonth).padStart(2, '0') : 'XX'}/{card.displayData?.expiryYear?.toString().slice(-2) || 'XX'}
                  </div>
                  {selectedCard?.cardId === card.cardId && (
                    <div className="mt-2 text-xs text-blue-600 font-medium">✓ Selected</div>
                  )}
                </div>
              ))}
              
              {/* Add New Card */}
              <div
                onClick={() => openCardModal('manage', null)}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-all flex flex-col items-center justify-center min-h-[120px] text-gray-600 hover:text-blue-600"
              >
                <Plus size={32} className="mb-2" />
                <span className="font-medium">Add Payment Method</span>
              </div>
            </div>
            
            {clientCards.length === 0 && tempCards.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <CreditCard size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No saved payment methods</p>
                <p className="text-sm mb-4">Add a card to get started with faster payments</p>
                <button
                  onClick={() => openCardModal('manage', null)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                >
                  Add Your First Card
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Policy Details with Premium Protection */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FileText size={20} className="text-blue-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-800">Policy Details</h2>
            </div>
            {paymentType === 'New Business' && premiumAmount > 0 && (
              <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded text-sm">
                Min: {formatCurrency(premiumAmount)}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Payment Type</label>
              <select
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="New Business">Insurance Premium</option>
                <option value="Monthly Payment">Monthly Payment</option>
                <option value="Endorsement">Endorsement</option>
              </select>
            </div>
            
            {paymentType === 'Endorsement' && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Policy Number *</label>
                <input
                  type="text"
                  value={policyNumber}
                  onChange={(e) => setPolicyNumber(e.target.value)}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Enter policy number (will be tagged in GHL)"
                  required
                />
                <p className="text-xs text-blue-600 mt-1">
                  📋 This policy number will automatically tag the client in Go High Level for account linking
                </p>
              </div>
            )}
            
            {/* Payment Amount Fields Based on Type */}
            {paymentType === 'Monthly Payment' && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Processing Fee</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-500">$</span>
                  </div>
                  <input
                    type="number"
                    value={5}
                    readOnly
                    className="w-full p-3 pl-8 bg-gray-100 border border-gray-200 rounded-lg text-gray-600"
                    placeholder="5.00"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <Lock size={16} className="text-gray-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Fixed processing fee for monthly payments</p>
              </div>
            )}

            {(paymentType === 'New Business' || paymentType === 'Endorsement') && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Insurance Premium
                  {paymentType === 'New Business' && <span className="text-red-500"> (Protected)</span>}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-500">$</span>
                  </div>
                  <input
                    type="number"
                    value={premium}
                    onChange={(e) => setPremium(e.target.value)}
                    min="0"
                    step="0.01"
                    className="w-full p-3 pl-8 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="0.00"
                  />
                  {paymentType === 'New Business' && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <Shield size={16} className="text-orange-500" />
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {(paymentType === 'New Business' || paymentType === 'Endorsement') && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Broker Fee</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-500">$</span>
                  </div>
                  <input
                    type="number"
                    value={brokerFee}
                    onChange={(e) => setBrokerFee(e.target.value)}
                    min="0"
                    step="0.01"
                    className="w-full p-3 pl-8 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Total Due Display */}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium text-blue-800">Total Due:</span>
              <span className="font-bold text-blue-800 text-xl">{formatCurrency(totalDue)}</span>
            </div>
            {paymentType === 'New Business' && premiumAmount > 0 && (
              <div className="text-xs text-orange-600 mt-1">
                ⚠️ Minimum payment: {formatCurrency(premiumAmount)}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Charge Now Section */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <CreditCard size={20} className="text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Charge Now</h2>
            {totalCcToComp > 0 && (
              <span className="ml-3 bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                CC to COMP: {formatCurrency(totalCcToComp)}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={addChargeNowSplit}
            className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 py-2 px-3 rounded-lg transition-all"
          >
            <Plus size={16} className="mr-1" />
            {chargeNowSplits.length > 1 ? 'Add Another Payment' : 'Add Payment'}
          </button>
        </div>
        
        {/* Info about payment methods */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 text-sm">
            <strong>Payment Methods:</strong> Credit Card (processed by us), Cash (no PIN required), CC to COMP (tracked, client pays carrier directly)
          </p>
        </div>
        
        {chargeNowSplits.length > 0 ? (
          <div className="space-y-3">
            {chargeNowSplits.map((split) => (
              <div key={split.id} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                {split.paymentMethod === 'cash' ? (
                  <div className="flex items-center">
                    <Banknote size={20} className="text-green-600 mr-1" />
                    <Lock size={16} className="text-orange-500" />
                  </div>
                ) : (
                  <CreditCard size={20} className="text-gray-400" />
                )}
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-500">$</span>
                  </div>
                  <input
                    type="number"
                    value={split.amount}
                    onChange={(e) => updateChargeNowSplit(split.id, 'amount', e.target.value)}
                    className="p-3 pl-8 bg-white border border-gray-200 rounded-lg w-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                
                <select
                  value={split.paymentMethod || 'card'}
                  onChange={(e) => updateChargeNowSplit(split.id, 'paymentMethod', e.target.value)}
                  className="p-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="card">Credit Card</option>
                  <option value="cash">💰 Cash (Requires PIN)</option>
                  <option value="cc_to_comp">🏢 CC to COMP (Client pays carrier)</option>
                </select>
                
                {split.paymentMethod === 'card' && (
                  <select
                    value={split.cardId}
                    onChange={(e) => {
                      if (e.target.value === 'new') {
                        openCardModal('chargeNow', split.id);
                      } else {
                        updateChargeNowSplit(split.id, 'cardId', e.target.value);
                      }
                    }}
                    className="flex-1 p-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="new">Enter New Card</option>
                    {getAllAvailableCards().map(card => (
                      <option key={card.cardId} value={card.cardId}>
                        {card.displayData?.cardType} ****{card.displayData?.last4}
                      </option>
                    ))}
                  </select>
                )}
                
                {split.paymentMethod === 'cash' && (
                  <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 font-medium">
                    🔒 PIN Authorized Cash Payment
                  </div>
                )}
                
                {split.paymentMethod === 'cc_to_comp' && (
                  <div className="flex-1 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 font-medium">
                    🏢 Client Pays Insurance Carrier Directly
                  </div>
                )}
                
                {chargeNowSplits.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeChargeNowSplit(split.id)}
                    className="p-2 hover:bg-gray-200 rounded-full transition-all"
                  >
                    <X size={18} className="text-gray-500" />
                  </button>
                )}
              </div>
            ))}
            
            <div className="flex justify-between text-sm font-medium p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <span className="text-blue-800">Total Charge Now:</span>
              <span className="text-blue-800 font-bold">{formatCurrency(totalChargeNow)}</span>
            </div>
          </div>
        ) : (
          <div className="text-center p-6 border border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-500">No immediate charges added</p>
          </div>
        )}
      </div>
      
      {/* Scheduled Payments Section - Only show for non-monthly payments */}
      {paymentType !== 'Monthly Payment' && (
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <CalendarDays size={20} className="text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Scheduled Payments</h2>
          </div>
          <button
            type="button"
            onClick={addScheduledPayment}
            className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 py-2 px-3 rounded-lg transition-all"
          >
            <Plus size={16} className="mr-1" />
            Add Scheduled Payment
          </button>
        </div>
        
        {/* Info about scheduled payments */}
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 text-sm">
            <strong>Scheduled Payments:</strong> Payments will be automatically processed on the selected date. Credit card payments are processed via saved cards, cash payments require manual PIN authorization.
          </p>
        </div>
        
        {scheduledPayments.length > 0 ? (
          <div className="space-y-3">
            {scheduledPayments.map((payment) => (
              <div key={payment.id} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <CalendarDays size={20} className="text-gray-400" />
                
                <input
                  type="date"
                  value={payment.date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => updateScheduledPayment(payment.id, 'date', e.target.value)}
                  className="p-3 bg-white border border-gray-200 rounded-lg w-40 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-500">$</span>
                  </div>
                  <input
                    type="number"
                    value={payment.amount}
                    onChange={(e) => updateScheduledPayment(payment.id, 'amount', e.target.value)}
                    className="p-3 pl-8 bg-white border border-gray-200 rounded-lg w-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    disabled={payment.isAutoCalculated}
                  />
                </div>
                
                <select
                  value={payment.paymentMethod || 'card'}
                  onChange={(e) => updateScheduledPayment(payment.id, 'paymentMethod', e.target.value)}
                  className="p-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="card">Credit Card</option>
                  <option value="cash">💰 Cash (Requires PIN)</option>
                </select>
                
                {payment.paymentMethod === 'card' && (
                  <select
                    value={payment.cardId}
                    onChange={(e) => {
                      if (e.target.value === 'new') {
                        openCardModal('scheduled', payment.id);
                      } else {
                        updateScheduledPayment(payment.id, 'cardId', e.target.value);
                      }
                    }}
                    className="flex-1 p-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="new">Enter New Card</option>
                    {getAllAvailableCards().map(card => (
                      <option key={card.cardId} value={card.cardId}>
                        {card.displayData?.cardType} ****{card.displayData?.last4}
                      </option>
                    ))}
                  </select>
                )}
                
                {payment.paymentMethod === 'cash' && (
                  <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 font-medium">
                    🔒 PIN Authorized Cash Payment
                  </div>
                )}
                
                {payment.paymentMethod === 'cc_to_comp' && (
                  <div className="flex-1 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 font-medium">
                    🏢 Client Pays Insurance Carrier Directly
                  </div>
                )}
                
                {payment.isAutoCalculated && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                    Auto
                  </span>
                )}
                
                {!payment.isAutoCalculated && (
                  <button
                    type="button"
                    onClick={() => removeScheduledPayment(payment.id)}
                    className="p-2 hover:bg-gray-200 rounded-full transition-all"
                  >
                    <X size={18} className="text-gray-500" />
                  </button>
                )}
              </div>
            ))}
            
            <div className="flex justify-between text-sm font-medium p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <span className="text-blue-800">Total Scheduled:</span>
              <span className="text-blue-800 font-bold">{formatCurrency(totalScheduled)}</span>
            </div>
          </div>
        ) : (
          <div className="text-center p-6 border border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-500">No scheduled payments added</p>
          </div>
        )}
      </div>
      )}
      
      {/* Payment Summary */}
      <div className={`mb-6 p-5 rounded-xl shadow-sm border ${
        isValid ? "bg-green-50 border-green-100" : "bg-red-50 border-red-100"
      }`}>
        <div className="flex items-center">
          {isValid 
            ? <CheckCircle size={24} className="text-green-600 mr-3" /> 
            : <AlertTriangle size={24} className="text-red-600 mr-3" />
          }
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Payment Summary</h2>
            <p className={isValid ? "text-green-700" : "text-red-700"}>{validationMessage}</p>
          </div>
        </div>
        
        <div className="mt-4 space-y-4">
          {/* Summary Grid */}
          <div className={`grid ${totalCcToComp > 0 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3'} gap-4`}>
            <div className="p-3 bg-white rounded-lg border border-gray-100">
              <div className="text-sm text-gray-600 mb-1">Total Amount Due</div>
              <div className="text-lg font-bold">{formatCurrency(totalDue)}</div>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-gray-100">
              <div className="text-sm text-gray-600 mb-1">Actual Charge (Our Side)</div>
              <div className="text-lg font-bold">{formatCurrency(actualChargeNow)}</div>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-gray-100">
              <div className="text-sm text-gray-600 mb-1">Actual Scheduled</div>
              <div className="text-lg font-bold">{formatCurrency(actualScheduled)}</div>
            </div>
            
            {totalCcToComp > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm text-blue-700 mb-1">CC to COMP (Tracked)</div>
                <div className="text-lg font-bold text-blue-800">{formatCurrency(totalCcToComp)}</div>
              </div>
            )}
          </div>
          
          {/* Detailed Breakdown */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-3">Payment Method Breakdown</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="font-medium text-gray-700 mb-2">Credit Cards (We Process):</div>
                <ul className="space-y-1 text-gray-600">
                  <li>• Charge Now: {formatCurrency(actualChargeNow)}</li>
                  <li>• Scheduled: {formatCurrency(actualScheduled)}</li>
                  <li className="font-medium">Total: {formatCurrency(actualChargeNow + actualScheduled)}</li>
                </ul>
              </div>
              
              <div>
                <div className="font-medium text-gray-700 mb-2">CC to COMP (Client Pays Carrier):</div>
                <ul className="space-y-1 text-gray-600">
                  <li>• Charge Now: {formatCurrency(ccToCompChargeNow)}</li>
                  <li>• Scheduled: {formatCurrency(ccToCompScheduled)}</li>
                  <li className="font-medium">Total: {formatCurrency(totalCcToComp)}</li>
                </ul>
              </div>
              
              <div>
                <div className="font-medium text-gray-700 mb-2">Balance Check:</div>
                <ul className="space-y-1 text-gray-600">
                  <li>• Total Due: {formatCurrency(totalDue)}</li>
                  <li>• Total Allocated: {formatCurrency(totalChargeNow + totalScheduled)}</li>
                  <li className={`font-medium ${Math.abs(totalShortfall) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                    • {Math.abs(totalShortfall) > 0.01 ? 'Remaining' : 'Balanced'}: {formatCurrency(Math.abs(totalShortfall))}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={!isValid || totalDue === 0 || isProcessing}
          className={`px-8 py-4 rounded-lg shadow transition-all text-lg font-medium ${
            isValid && totalDue > 0 && !isProcessing
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isProcessing ? 'Creating Payment Plan...' : `Create Payment Plan ${formatCurrency(totalDue)}`}
        </button>
      </div>
      
      {/* PIN Authorization Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-4 pb-2 border-b">
              <div className="flex items-center">
                <Lock size={20} className="text-orange-500 mr-2" />
                <h3 className="text-lg font-bold text-gray-800">PIN Authorization Required</h3>
              </div>
              <button 
                onClick={() => setShowPinModal(false)}
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">{pinPurpose}</p>
              <label className="block text-sm font-medium text-gray-600 mb-2">Enter PIN:</label>
              <input
                type="password"
                value={pinCode}
                onChange={(e) => setPinCode(e.target.value)}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-center text-xl tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={6}
                placeholder="••••••"
              />
              {pinError && (
                <p className="text-red-600 text-sm mt-2">{pinError}</p>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowPinModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={validatePin}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
              >
                Authorize
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Card Entry Modal */}
      {showCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <CreditCard size={24} className="text-blue-600 mr-3" />
                  <h3 className="text-xl font-bold text-gray-800">Add Payment Card</h3>
                </div>
                <button 
                  onClick={() => setShowCardModal(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-all"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                All card information is encrypted and stored securely
              </p>
            </div>
            
            {/* Body */}
            <div className="px-6 py-6">
              {/* Card Information Section */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <Shield size={20} className="text-green-600 mr-2" />
                  Card Information
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Card Number *</label>
                    <input
                      type="text"
                      value={newCardData.cardNumber}
                      onChange={(e) => setNewCardData({...newCardData, cardNumber: e.target.value.replace(/\D/g, '')})}
                      placeholder="1234 5678 9012 3456"
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-lg font-mono"
                      maxLength={16}
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name *</label>
                    <input
                      type="text"
                      value={newCardData.cardName}
                      onChange={(e) => setNewCardData({...newCardData, cardName: e.target.value})}
                      placeholder="John Smith"
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Month *</label>
                    <select
                      value={newCardData.expiryMonth}
                      onChange={(e) => setNewCardData({...newCardData, expiryMonth: e.target.value})}
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">Select Month</option>
                      {Array.from({length: 12}, (_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                          {String(i + 1).padStart(2, '0')} - {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Year *</label>
                    <select
                      value={newCardData.expiryYear}
                      onChange={(e) => setNewCardData({...newCardData, expiryYear: e.target.value})}
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">Select Year</option>
                      {Array.from({length: 10}, (_, i) => {
                        const year = new Date().getFullYear() + i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">CVV *</label>
                    <input
                      type="text"
                      value={newCardData.cvv}
                      onChange={(e) => setNewCardData({...newCardData, cvv: e.target.value.replace(/\D/g, '')})}
                      placeholder="123"
                      className="w-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-mono"
                      maxLength={4}
                    />
                    <p className="text-sm text-gray-600 mt-1">3-4 digits on the back of your card</p>
                  </div>
                </div>
              </div>
              
              {/* Billing Address Section */}
              <div className="border-t pt-8">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <User size={20} className="text-blue-600 mr-2" />
                  Billing Address
                </h4>
                <p className="text-sm text-gray-600 mb-6">
                  This information is required for payment processing and security verification
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Street Address *</label>
                    <input
                      type="text"
                      value={newCardData.address}
                      onChange={(e) => setNewCardData({...newCardData, address: e.target.value})}
                      placeholder="123 Main Street"
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                    <input
                      type="text"
                      value={newCardData.city}
                      onChange={(e) => setNewCardData({...newCardData, city: e.target.value})}
                      placeholder="Los Angeles"
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                    <input
                      type="text"
                      value={newCardData.state}
                      onChange={(e) => setNewCardData({...newCardData, state: e.target.value.toUpperCase()})}
                      placeholder="CA"
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      maxLength={2}
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                    <input
                      type="text"
                      value={newCardData.zip}
                      onChange={(e) => setNewCardData({...newCardData, zip: e.target.value.replace(/\D/g, '')})}
                      placeholder="90210"
                      className="w-48 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t rounded-b-xl">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600 flex items-center">
                  <Lock size={16} className="text-green-600 mr-1" />
                  Secured with 256-bit encryption
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCardModal(false)}
                    className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveNewCard}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium flex items-center"
                  >
                    <CreditCard size={16} className="mr-2" />
                    Save Card Securely
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Success Modal with Receipt Download */}
      {showSuccessModal && lastTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-2xl">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <CheckCircle size={24} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Payment Processed Successfully!</h3>
              <p className="text-gray-600">Transaction ID: {lastTransaction.transactionId}</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="font-semibold ml-2">{formatCurrency(lastTransaction.totalAmount)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-semibold ml-2 text-green-600">{formatCurrency(lastTransaction.totalPaid)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Status:</span>
                  <span className="font-semibold ml-2 capitalize">{lastTransaction.status}</span>
                </div>
                {lastTransaction.ccToCompRequired && (
                  <div>
                    <span className="text-gray-600">CC to COMP:</span>
                    <span className="font-semibold ml-2 text-blue-600">{formatCurrency(lastTransaction.ccToCompAmount)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Breakdown */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">Payment Breakdown</h4>
              
              {/* Charge Now Section */}
              {lastTransaction.chargeNowPayments && lastTransaction.chargeNowPayments.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-gray-700">Charged Now ({lastTransaction.chargeNowCount})</h5>
                    <span className="font-semibold text-green-600">{formatCurrency(lastTransaction.chargeNowTotal)}</span>
                  </div>
                  <div className="space-y-2">
                    {lastTransaction.chargeNowPayments.map((payment, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center">
                          {payment.paymentMethod === 'cash' ? '💰' : payment.paymentMethod === 'cc_to_comp' ? '🏢' : '💳'}
                          <span className="ml-2 text-sm">
                            {payment.paymentMethod === 'cash' ? 'Cash Payment' : 
                             payment.paymentMethod === 'cc_to_comp' ? 'CC to COMP' : 'Card Payment'}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">{formatCurrency(payment.amount)}</div>
                          <div className={`text-xs ${payment.status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                            {payment.status}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Scheduled Section */}
              {lastTransaction.scheduledPayments && lastTransaction.scheduledPayments.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-gray-700">Scheduled ({lastTransaction.scheduledCount})</h5>
                    <span className="font-semibold text-blue-600">{formatCurrency(lastTransaction.scheduledTotal)}</span>
                  </div>
                  <div className="space-y-2">
                    {lastTransaction.scheduledPayments.map((payment, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                        <div className="flex items-center">
                          <CalendarDays size={16} className="text-blue-600" />
                          <span className="ml-2 text-sm">
                            {payment.paymentMethod === 'cash' ? 'Cash Payment' : 
                             payment.paymentMethod === 'cc_to_comp' ? 'CC to COMP' : 'Card Payment'}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">{formatCurrency(payment.amount)}</div>
                          <div className="text-xs text-blue-600">
                            {new Date(payment.date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {lastTransaction.completedPayments && lastTransaction.completedPayments.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Download Receipts</h4>
                <div className="space-y-2">
                  {lastTransaction.completedPayments.map((payment, index) => (
                    <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <FileText size={20} className="text-gray-600 mr-3" />
                        <div>
                          <div className="font-medium">
                            {payment.paymentMethod === 'cash' ? '💰 Cash Payment' : 
                             payment.paymentMethod === 'cc_to_comp' ? '🏢 CC to COMP' :
                             '💳 Card Payment'}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatCurrency(payment.amount)} - {new Date(payment.processedAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => downloadReceipt(payment.id)}
                        className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm"
                      >
                        <FileText size={16} className="mr-1" />
                        Download PDF
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-between">
              {/* Only show payment plan button if there are scheduled payments */}
              {lastTransaction.scheduledPayments && lastTransaction.scheduledPayments.length > 0 && (
                <button
                  onClick={() => downloadPaymentPlan(lastTransaction.transactionId)}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all flex items-center shadow-md"
                >
                  <FileText size={16} className="mr-2" />
                  Print Payment Plan
                </button>
              )}
              <button
                onClick={resetForm}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
              >
                New Transaction
              </button>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedPaymentPortal;